/**
 * Module Editor Registry
 *
 * This file provides a registry of content editors for different module types.
 * Each editor is lazy-loaded for optimal code splitting.
 *
 * To add a new editor:
 * 1. Create a new editor component in this folder (e.g., MyEditor.js)
 * 2. Add it to the ModuleEditors object below
 * 3. Update moduleConfig.js to reference your new editor type
 *
 * Editor Interface:
 * Each editor component receives these props:
 * - module: Object - The full module object being edited
 * - initialData: Object - The module's current data (module.data)
 * - onChange: (data: Object) => void - Call this when content changes
 * - moduleType: string - The module type name (e.g., 'text', 'image')
 *
 * The editor should call onChange with the new data structure whenever
 * content changes. The data structure should match what the module expects:
 * { content: any, params: Object }
 */

import React from 'react';

/**
 * Registry of module content editors
 * Maps editor type (from moduleConfig.contentEditor) to lazy-loaded component
 */
export const ModuleEditors = {
  richText: React.lazy(() => import('./RichTextEditor')),
  media: React.lazy(() => import('./MediaEditor')),
  embed: React.lazy(() => import('./EmbedEditor')),
  task: React.lazy(() => import('./TaskEditor')),
  slideshow: React.lazy(() => import('./SlideshowEditor')),
  webinar: React.lazy(() => import('./WebinarEditor')),
  discussion: React.lazy(() => import('./DiscussionEditor')),
};

/**
 * Get the editor component for a given editor type
 * @param {string} editorType - The editor type from moduleConfig.contentEditor
 * @returns {React.LazyExoticComponent|null} The lazy-loaded editor component or null
 */
export const getEditorComponent = (editorType) => {
  return ModuleEditors[editorType] || null;
};

/**
 * Check if an editor exists for the given type
 * @param {string} editorType - The editor type to check
 * @returns {boolean}
 */
export const hasEditor = (editorType) => {
  return editorType in ModuleEditors;
};

export default ModuleEditors;
